import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getEmployees, getScreenshots, uploadScreenshot, getActiveEntry, getSettings } from '../api';
import { formatDateTime, getInitials } from '../utils';
import { useAuth } from '../AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function Screenshots() {
  const { user, isAdmin, isManager } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState('');
  const [screenshots, setScreenshots] = useState([]);
  const [interval, setIntervalVal] = useState(5);
  const [capturing, setCapturing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [allowed, setAllowed] = useState(true); // permission gate
  const captureRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    const setup = async () => {
      // Managers see their team's screenshots via the employee selector
      if (isManager) {
        const emps = await getEmployees(); // backend returns only team members
        setEmployees(emps);
        if (emps.length) setSelectedEmp(emps[0].id);
        return;
      }
      // Plain employees: check permission setting
      if (!isAdmin && user?.employee_id) {
        try {
          const settings = await getSettings();
          const canSee = settings.perm_employee_see_screenshots === 'true' || settings.perm_employee_see_screenshots === true;
          setAllowed(canSee);
          if (!canSee) return;
        } catch {}
        setEmployees([]);
        setSelectedEmp(user.employee_id);
        return;
      }
      const emps = await getEmployees();
      setEmployees(emps);
      if (emps.length) setSelectedEmp(emps[0].id);
    };
    setup();
  }, [isAdmin, isManager, user?.employee_id]);

  const loadScreenshots = useCallback(async () => {
    if (!selectedEmp) return;
    const data = await getScreenshots({ employee_id: selectedEmp });
    setScreenshots(data);
  }, [selectedEmp]);

  useEffect(() => { loadScreenshots(); }, [loadScreenshots]);

  const captureScreenshot = useCallback(async () => {
    if (!selectedEmp) return;
    const canvas = document.createElement('canvas');
    const video = videoRef.current;
    if (!video || !streamRef.current) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const fd = new FormData();
      fd.append('screenshot', blob, 'capture.png');
      fd.append('employee_id', selectedEmp);
      const activeEntry = await getActiveEntry(selectedEmp);
      if (activeEntry) fd.append('time_entry_id', activeEntry.id);
      // Use real activity score from the global monitor if available, else fallback
      const score = window.__getActivityScore ? window.__getActivityScore(300000) : Math.floor(Math.random() * 40) + 60;
      fd.append('activity_score', String(score));
      await uploadScreenshot(fd);
      await loadScreenshots();
    }, 'image/png');
  }, [selectedEmp, loadScreenshots]);

  const startCapture = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setCapturing(true);
      await captureScreenshot();
      captureRef.current = setInterval(captureScreenshot, interval * 60 * 1000);
    } catch (e) {
      alert('Screen capture permission denied or not supported.');
    }
  }, [interval, captureScreenshot]);

  const stopCapture = () => {
    clearInterval(captureRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCapturing(false);
  };

  useEffect(() => () => stopCapture(), []);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Screenshots</h1>
      </div>

      {!allowed && !isAdmin && (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>🔒</div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Access Restricted</div>
          <div className="text-muted">Your administrator has not enabled screenshot viewing for employees. Contact your admin to request access.</div>
        </div>
      )}

      {(allowed || isAdmin) && <>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">Capture Settings</div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {(isAdmin || isManager) && <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Employee</label>
            <select value={selectedEmp} onChange={e => setSelectedEmp(e.target.value)}>
              {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Interval (minutes)</label>
            <select value={interval} onChange={e => setIntervalVal(Number(e.target.value))}>
              {[1, 2, 5, 10, 15, 30].map(n => <option key={n} value={n}>{n} min</option>)}
            </select>
          </div>
          {capturing ? (
            <button className="btn btn-danger" onClick={stopCapture}>■ Stop Capture</button>
          ) : (
            <button className="btn btn-success" onClick={startCapture}>▶ Start Capture</button>
          )}
          {capturing && <span className="badge badge-green timer-pulse">● Recording every {interval}m</span>}
        </div>
        <video ref={videoRef} style={{ display: 'none' }} muted />
      </div>

      <div className="card">
        <div className="card-title">Captured Screenshots ({screenshots.length})</div>
        {screenshots.length === 0 ? (
          <div className="empty">No screenshots yet. Start capture to begin.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
            {screenshots.map(ss => (
              <div key={ss.id} style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', cursor: 'pointer' }}
                onClick={() => setPreview(ss)}>
                <img
                  src={`${API_BASE}/screenshots/${ss.filename}`}
                  alt="screenshot"
                  style={{ width: '100%', height: 160, objectFit: 'cover', background: '#f1f5f9', display: 'block' }}
                  onError={e => { e.target.style.display = 'none'; }}
                />
                <div style={{ padding: '8px 10px' }}>
                  <div style={{ fontWeight: 500, fontSize: '0.8rem' }}>{ss.employee_name}</div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>{formatDateTime(ss.captured_at)}</div>
                  <div style={{ fontSize: '0.75rem', marginTop: 2 }}>Activity: <strong>{ss.activity_score}%</strong></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {preview && (
        <div className="modal-overlay" onClick={() => setPreview(null)}>
          <div style={{ background: 'var(--card)', borderRadius: 16, padding: 20, maxWidth: '90vw', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>{preview.employee_name} · {formatDateTime(preview.captured_at)}</div>
            <img src={`${API_BASE}/screenshots/${preview.filename}`} alt="screenshot" style={{ maxWidth: '80vw', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 }} />
            <div style={{ marginTop: 10 }}>Activity Score: <strong>{preview.activity_score}%</strong></div>
          </div>
        </div>
      )}
      </>}
    </div>
  );
}
